import { useEffect } from 'react';
import { CRM_MARKETING_SITE_URL } from '../config/marketingRoutes';

const SITE_URL =
  import.meta.env.VITE_SITE_URL?.trim().replace(/\/+$/, '') ||
  CRM_MARKETING_SITE_URL;
const DEFAULT_OG_IMAGE = `${SITE_URL}/graphics/og-frontpage.png`;

interface PageMetadataInput {
  title: string;
  description: string;
  path: string;
}

interface StructuredDataInput {
  id: string;
  data: Record<string, unknown>;
}

function upsertMeta(
  attribute: 'name' | 'property',
  value: string,
  content: string
): void {
  const selector = `meta[${attribute}="${value}"]`;
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }

  element.content = content;
}

function upsertCanonical(url: string): void {
  let canonical = document.head.querySelector(
    'link[rel="canonical"]'
  ) as HTMLLinkElement | null;

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.rel = 'canonical';
    document.head.appendChild(canonical);
  }

  canonical.href = url;
}

function normalizePath(path: string): string {
  if (!path.startsWith('/')) {
    return `/${path}`;
  }
  return path;
}

export function buildSiteUrl(path: string): string {
  return `${SITE_URL}${normalizePath(path)}`;
}

export function usePageMetadata({
  title,
  description,
  path,
}: PageMetadataInput): void {
  useEffect(() => {
    const canonicalUrl = buildSiteUrl(path);

    document.title = title;

    upsertMeta('name', 'description', description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', 'Inmueble.red');
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertMeta('property', 'og:image', DEFAULT_OG_IMAGE);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', DEFAULT_OG_IMAGE);

    upsertCanonical(canonicalUrl);
  }, [description, path, title]);
}

export function useStructuredData({ id, data }: StructuredDataInput): void {
  useEffect(() => {
    const scriptId = `structured-data-${id}`;
    let script = document.head.querySelector(
      `#${scriptId}`
    ) as HTMLScriptElement | null;

    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }

    script.text = JSON.stringify(data);

    return () => {
      script?.remove();
    };
  }, [data, id]);
}
